import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva, VariantProps } from "class-variance-authority";

import { cn } from "@src/lib/utils/cn";

// 14px medium (compact-title) in text-primary. A label placed after a `peer`
// control (Checkbox, Switch) dims with it when the control is disabled.
const labelVariants = cva(
  "font-sans-v4 text-s-v4 font-medium-v4 text-text-primary peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
);

export interface LabelProps
  extends React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>,
    VariantProps<typeof labelVariants> {
  /**
   * Appends a status-error asterisk. Visual only: also set `required` (or
   * aria-required) on the control so assistive tech announces it.
   */
  required?: boolean;
  /** Appends a quiet "(optional)" after the label text. */
  optional?: boolean;
}

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  LabelProps
>(({ className, required, optional, children, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(labelVariants(), className)}
    {...props}
  >
    {children}
    {required && (
      <span aria-hidden="true" className="ml-0.5 text-status-error-text">
        *
      </span>
    )}
    {optional && !required && (
      <>
        {" "}
        <span className="font-normal-v4 text-text-tertiary">(optional)</span>
      </>
    )}
  </LabelPrimitive.Root>
));
Label.displayName = LabelPrimitive.Root.displayName;

export { Label };
